import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { TreeDataNode } from 'antd'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerSection,
  DrawerDetailRow,
  DrawerFooter,
} from '@/components/ui/drawer'
import { getPermissionById } from '@/api/permissions.api'
import type { PermissionGroupItem } from '@/types/api'
import { PermissionGrid } from '@/components/form/PermissionGrid'
import { permissionsAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'

interface PermissionDetailDrawerProps {
  permissionId: string | null
  onClose: () => void
  onUpdate?: (permission: PermissionGroupItem) => void
  showUnitColumn?: boolean
}

export function PermissionDetailDrawer({
  permissionId,
  onClose,
  onUpdate,
  showUnitColumn,
}: PermissionDetailDrawerProps) {
  const permissionUser = useAtomValue(permissionsAtom)
  const { data: permission, isLoading } = useQuery({
    queryKey: ['permission-detail', permissionId],
    queryFn: ({ signal }) =>
      permissionId
        ? getPermissionById(permissionId, signal)
        : Promise.resolve(null),
    enabled: !!permissionId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const treeData: TreeDataNode[] = useMemo(() => {
    if (!permission) return []

    // Tiến hành lọc logic phân quyền cây dựa theo tài khoản đang đăng nhập khi !showUnitColumn
    const filteredCategories = permission.categoryPermissionList
      .map((cat) => {
        const filteredPermissions = cat.permissionList.filter((p) => {
          if (!showUnitColumn) {
            return permissionUser.includes(p.code)
          }
          return true
        })
        return {
          ...cat,
          permissionList: filteredPermissions,
        }
      })
      .filter((cat) => cat.permissionList.length > 0)

    if (filteredCategories.length === 0) return []

    return [
      {
        title: 'Tất cả',
        key: 'all',
        children: filteredCategories.map((cat) => ({
          title: cat.name,
          key: cat.id,
          children: [...cat.permissionList].map((p) => ({
            title: p.name,
            key: p.id,
          })),
        })),
      },
    ]
  }, [permission, showUnitColumn, permissionUser])

  const checkedKeys = useMemo(() => {
    if (!permission) return []
    return [...permission.categoryPermissionList]
      .flatMap((cat) => [...cat.permissionList])
      .filter((p) => {
        // Chỉ tính toán check đối với những quyền thực sự được hiển thị công khai cho user
        if (!showUnitColumn) {
          return p.isSelected && permissionUser.includes(p.code)
        }
        return p.isSelected
      })
      .map((p) => p.id)
  }, [permission, showUnitColumn, permissionUser])

  const unitNames = useMemo(() => {
    if (!permission) return ''
    if (permission.isAllUnit) return 'Tất cả đơn vị'
    return [...permission.unitList].map((u) => u.name).join(', ')
  }, [permission])

  return (
    <Drawer
      open={!!permissionId}
      onClose={onClose}
      title="CHI TIẾT NHÓM QUYỀN"
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => permission && onUpdate?.(permission)}
            className="min-w-[160px] h-10"
            disabled={!permission || isLoading}
          >
            Cập nhật
          </Button>
        </DrawerFooter>
      }
    >
      {isLoading && (
        <div className="p-6 text-center text-gray-500">
          Đang tải chi tiết...
        </div>
      )}

      {permission && (
        <div className="space-y-6">
          <DrawerSection title="Thông tin chung">
            <DrawerDetailRow label="Tên nhóm quyền:" value={permission.name} />
            {showUnitColumn && (
              <DrawerDetailRow
                label="Đơn vị áp dụng:"
                value={unitNames || '--'}
                alignTop
              />
            )}
            <DrawerDetailRow
              label="Trạng thái:"
              value={
                <span
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                    permission.status === 1
                      ? 'bg-[#EAF7ED] text-[#2FB14F]'
                      : 'bg-[#FDECEB] text-[#EE4036]'
                  }`}
                >
                  {permission.status === 1 ? 'Hoạt động' : 'Không hoạt động'}
                </span>
              }
            />
            <DrawerDetailRow
              label="Ghi chú:"
              value={permission.description || '--'}
              alignTop
            />
          </DrawerSection>

          <DrawerSection title="Phân quyền">
            {treeData.length > 0 ? (
              <PermissionGrid
                disabled
                treeData={treeData}
                value={checkedKeys}
              />
            ) : (
              <span className="text-gray-500">
                Chưa có thông tin phân quyền hoặc bạn không có quyền xem.
              </span>
            )}
          </DrawerSection>
        </div>
      )}
    </Drawer>
  )
}
